import { usePostHog } from "posthog-js/react";
import { useCallback, useRef, useEffect, useState } from "react";
import { useUserProfile } from "../providers/UserProfileProvider";
import {
  RemainingCreditsProvider,
  useRemainingCredits,
} from "../providers/RemainingCreditsProvider";

// Define analytics event types
export type ANALYTICS_TYPES = {
  signupMethod: "google" | "apple" | "email" | "magic_link";
  subscriptionStatus: "free" | "paid";
  language: string;
  microphone: string;
  onboardingStep: "login" | "permissions" | "complete";
};

export const useAnalytics = () => {
  const posthog = usePostHog();
  const { userProfile } = useUserProfile();
  const { usage, limits } = useRemainingCredits();
  const lastIdentifiedUser = useRef<string | null>(null);
  const lastIdentifiedProperties = useRef<string | null>(null);
  const [appVersion, setAppVersion] = useState<string | null>(null);

  // Fetch app version on mount
  useEffect(() => {
    // Web version - no electron API
    // window.electronAPI.getUpdateStatus().then(status => {
    //   setAppVersion(status.currentVersion);
    // }).catch(error => {
    //   console.error("Failed to get app version:", error);
    // });
    setAppVersion("web");
  }, []);

  // Identify user with PostHog
  const identifyUser = useCallback(() => {
    if (!userProfile) return;

    const userProperties = {
      id: userProfile.id,
      email: userProfile.email,
      full_name: userProfile.full_name,
      created_at: userProfile.created_at,
      subscription_status: userProfile.Billing?.subscription_status || "free",
      plan_name: userProfile.Billing?.plan_name || "FREE",
      subscription_count: userProfile.Billing?.subscription_count,
      app_version: appVersion || "unknown",
    };

    // Create a hash of the properties to check if they've changed
    const propertiesHash = JSON.stringify(userProperties);
    
    // Only identify if the user ID or properties have changed
    if (lastIdentifiedUser.current === userProfile.id && 
        lastIdentifiedProperties.current === propertiesHash) {
      return; // Skip duplicate identification
    }

    posthog?.identify(userProfile.id, userProperties);
    lastIdentifiedUser.current = userProfile.id;
    lastIdentifiedProperties.current = propertiesHash;
  }, [posthog, userProfile, appVersion]);

  // Reset analytics on logout
  const resetAtLogout = useCallback(() => {
    posthog?.reset();
    lastIdentifiedUser.current = null;
    lastIdentifiedProperties.current = null;
  }, [posthog]);

  // ===== WEBAPP EVENTS WITH PREFIX =====
  
  // WEBAPP Login Selected
  const webappLoginSelected = useCallback(
    (loginMethod: ANALYTICS_TYPES["signupMethod"]) => {
      posthog?.capture("WEBAPP Login Selected", {
        loginMethod,
        timestamp: new Date().toISOString(),
      });
    },
    [posthog]
  );

  // WEBAPP Login Successful
  const webappLoginSuccessful = useCallback(
    (loginMethod: ANALYTICS_TYPES["signupMethod"]) => {
      posthog?.capture("WEBAPP Login Successful", {
        loginMethod,
        timestamp: new Date().toISOString(),
      });
    },
    [posthog]
  );

  // WEBAPP User Started Dictation
  const webappUserStartedDictation = useCallback(
    (language: string, microphone: string) => {
      posthog?.capture("WEBAPP User Started Dictation", {
        language,
        microphone,
        timestamp: new Date().toISOString(),
        remainingWords: usage?.remainingWords,
        subscriptionStatus: userProfile?.Billing?.subscription_status || "free",
      });
    },
    [posthog, usage, userProfile]
  );

  // WEBAPP User Stopped Dictation
  const webappUserStoppedDictation = useCallback(
    (data: {
      wordsSpoken: number;
      durationSeconds: number;
      enhanced: boolean;
      language: string;
      cancelled: boolean;
    }) => {
      posthog?.capture("WEBAPP User Stopped Dictation", {
        ...data,
        timestamp: new Date().toISOString(),
        remainingWords: usage?.remainingWords,
        subscriptionStatus: userProfile?.Billing?.subscription_status || "free",
        wpm:
          data.durationSeconds > 0
            ? Math.round((data.wordsSpoken * 60) / data.durationSeconds)
            : 0,
      });
    },
    [posthog, usage, userProfile]
  );

  // WEBAPP User Hit Their Limits
  const webappUserHitTheirLimits = useCallback(() => {
    posthog?.capture("WEBAPP User Hit Their Limits", {
      timestamp: new Date().toISOString(),
      planName: userProfile?.Billing?.plan_name || "FREE",
      wordsUsed: usage?.consumedWords,
      remainingWords: usage?.remainingWords,
      wordLimit: limits?.isUnlimited ? "unlimited" : limits?.wordLimit,
    });
  }, [posthog, userProfile, usage, limits]);

  // WEBAPP Dictation Enhanced
  const webappDictationEnhanced = useCallback(
    (data: {
      originalLength: number;
      enhancedLength: number;
      language: string;
    }) => {
      posthog?.capture("WEBAPP Dictation Enhanced", {
        ...data,
        timestamp: new Date().toISOString(),
        improvementRatio:
          data.originalLength > 0
            ? data.enhancedLength / data.originalLength
            : 0,
      });
    },
    [posthog]
  );

  // WEBAPP Microphone Changed
  const webappMicrophoneChanged = useCallback(
    (data: { oldMicrophone: string; newMicrophone: string }) => {
      posthog?.capture("WEBAPP Microphone Changed", {
        ...data,
        timestamp: new Date().toISOString(),
      });
    },
    [posthog]
  );

  // WEBAPP Language Changed
  const webappLanguageChanged = useCallback(
    (data: { oldLanguage: string; newLanguage: string }) => {
      posthog?.capture("WEBAPP Language Changed", {
        ...data,
        timestamp: new Date().toISOString(),
      });
    },
    [posthog]
  );

  // WEBAPP User Translated Text
  const webappUserTranslatedText = useCallback(
    (data: { 
      targetLanguage: string;
      textLength: number;
    }) => {
      posthog?.capture("WEBAPP User Translated Text", {
        ...data,
        timestamp: new Date().toISOString(),
      });
    },
    [posthog]
  );

  // WEBAPP User Used Rewrite
  const webappUserUsedRewrite = useCallback(
    (data: {
      rewriteType: string;
      textLength: number;
    }) => {
      posthog?.capture("WEBAPP User Used Rewrite", {
        ...data,
        timestamp: new Date().toISOString(),
      });
    },
    [posthog]
  );

  return {
    identifyUser,
    resetAtLogout,
    // WEBAPP events with prefix
    webappLoginSelected,
    webappLoginSuccessful,
    webappUserStartedDictation,
    webappUserStoppedDictation,
    webappUserHitTheirLimits,
    webappDictationEnhanced,
    webappMicrophoneChanged,
    webappLanguageChanged,
    webappUserTranslatedText,
    webappUserUsedRewrite,
  };
};
